import styles from "./OrganizerEventPage.module.scss";
import { InfoField } from "../components/Fields/InfoField/InfoField";
import searchIcon from "../image/search.svg";
import clearIcon from "../image/clear.svg";
import sendIcon from "../image/send.svg";
import { useEffect, useState } from "react";
import { EventOrganizer } from "../../../types/types";
import { useNavigate, useParams } from "react-router-dom";
import { EventImages } from "../components/EventImages/EventImages";
import { EventTitle } from "../components/EventTitle/EventTitle";
import { EventCategory } from "../components/EventCategory/EventCategory";
import { EventDatePlace } from "../components/EventDatePlace/EventDatePlace";
import { ContactField } from "../components/Fields/ContactField/ContactField";
import { LinkField } from "../components/Fields/LinkField/LinkField";
import { useWidth } from "../../../hooks/useWidth";
import Loader from "../../../components/Loader/Loader";
import { FinishEventModal } from "../components/FinishEventModal/FinishEventModal";
import { checkAuth, getAuthToken } from "../../../utils/utils";
import { API_BASE } from "../../../config/api";

export const OrganizerEventPage = () => {
  const [event, setEvent] = useState<EventOrganizer>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRepost, setIsRepost] = useState<boolean>(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const width = useWidth();
  const url = `${API_BASE}`;
  const { id } = useParams();

  useEffect(() => {
    if (!checkAuth()) {
      navigate("/registrationPage");
    }
  }, [navigate]);
  const token = getAuthToken();

  useEffect(() => {
    if (!id || id.trim() === "") {
      console.error("Неверный параметр события");
      navigate("/");
    }
  }, [id, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetch(`${url}/events/${id}/`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            authorization: `Bearer ${token}`,
          },
        }).then((res) => res.json());

        if (
          !data ||
          data.length === 0 ||
          data.detail === "Событие не найдено."
        ) {
          throw new Error("Событие не найдено");
        }

        setEvent(data);
        console.log("Событие:", data);
      } catch (error: any) {
        console.error("Ошибка загрузки данных:", error);
        setError(error.message || "Ошибка при загрузке данных");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, token, url]);

  useEffect(() => {
    if (error && error !== "Событие не найдено") {
      alert(error);
      navigate("/"); // Перенаправление на главную страницу
    }
  }, [error, navigate]);

  const link: string = `${window.location.origin}/event/${id}/`;

  const handleNavigate = () => {
    navigate(`/organizer_event/${id}/check_tickets`);
  };

  const handleFinished = () => {
    setIsFinishModalOpen(true);
  };

  const handleRepost = async () => {
    if (!isRepost) {
      setIsRepost(true);
    }

    try {
      await navigator.clipboard.writeText(link || "");
    } catch (error) {
      alert("Ошибка копирования:");
    }
  };

  if (loading) return <Loader />;
  if (!event || error === "Событие не найдено") {
    navigate("/404");
    return null;
  }
  if (!event.is_owner) {
    navigate(`/event/${id}`);
  }

  if (event.review_data === null) {
    event.review_data = { id: 0, url: "" };
  }

  const totalTickets = event.ticket_types_data.reduce(
    (sum, ticket) => sum + ticket.total_quantity,
    0
  );
  const totalSoldTickets = event.ticket_types_data.reduce(
    (sum, ticket) => sum + ticket.sold_count,
    0
  );
  let status = event?.status_info?.name as
    | "Активно"
    | "На модерации"
    | "Завершено"
    | "Прекращено"
    | "Отменено"
    | "Черновик";
  if (event.is_finished) {
    status = "Завершено";
  }
  if (event.is_canceled) {
    status = "Отменено";
  }

  const organizerEmail =
    event.owner_info?.email || localStorage.getItem("userEmail") || "";

  return (
    <div className={styles.organizerEventPage}>
      <section className={styles.leftBlock}>
        <EventImages
          mainImage={event.cover_medium}
          images={event.photos_data}
        />
        <div className={styles.buttonsWrapper}>
          <button
            style={width > 1024 ? { width: "211px" } : {}}
            className={styles.btnOutline}
            disabled={event.is_finished || event.is_canceled}
            onClick={handleNavigate}
          >
            <span className={styles.btnText}>
              {width < 1024 ? "Билет" : "Проверить билет"}
            </span>
            <img
              className={styles.btnIcon}
              src={searchIcon}
              alt="Иконка поиска"
            />
          </button>
          <button
            style={width > 1024 ? { width: "276px" } : {}}
            className={styles.btnOutline}
            disabled={event.is_finished || event.is_canceled}
            onClick={handleFinished}
          >
            <span className={styles.btnText}>
              {width < 1024 ? "Завершить" : "Завершить мероприятие"}
            </span>
            <img
              className={styles.btnIcon}
              src={clearIcon}
              alt="Иконка очистки"
            />
          </button>
        </div>
      </section>
      <section className={styles.rightBlock}>
        <EventTitle
          id={id}
          title={event.name}
          tickets={event.ticket_types_data}
          blockButton={event.is_canceled || event.is_finished}
          btnType="edit"
        />
        <EventCategory categories={event.categories_info} status={status} />
        <div className={styles.soldTicketsWrapper}>
          <InfoField name="Всего билетов" content={totalTickets} />
          <InfoField name="Продано" content={totalSoldTickets} />
          {event.ticket_types_data.map((ticket, index) => (
            <InfoField
              key={ticket.id}
              name={ticket.name}
              content={ticket.sold_count}
            />
          ))}
        </div>
        <EventDatePlace
          starts_at={event.starts_at}
          ends_at={event.ends_at || ""}
          place={event.place || {}}
        />
        <div className={styles.bottomWrapper}>
          <div className={styles.email_and_phoneWrapper}>
            <ContactField
              name="Email"
              nameMob="Контактная почта"
              content={organizerEmail}
            />
            <ContactField
              name="Телефон"
              nameMob="Контактный телефон"
              content={event.phone || ""}
            />
          </div>
          <p className={styles.description}>{event.description || ""}</p>
          <div className={styles.socialWrapper}>
            {event.social_media_links_data?.map((soc_media, index) => (
              <LinkField
                key={index}
                name="Ссылка на соц.сеть"
                content={soc_media.url}
              />
            ))}
            <LinkField
              name="Ссылка на отзывы"
              content={event.review_data.url || ""}
            />
          </div>
          <div className={styles.ticketWrapper}>
            {event.ticket_types_data &&
              event.ticket_types_data.map((ticket, index) => (
                <InfoField
                  key={ticket.id}
                  style={
                    width < 900
                      ? { marginRight: "auto", paddingRight: "10px" }
                      : { paddingRight: "10px" }
                  }
                  name={ticket.name}
                  content={ticket.total_quantity - ticket.sold_count}
                />
              ))}
          </div>
        </div>
        <div className={styles.btnWrapper}>
          {isRepost ? (
            <div className={styles.btnRepostActive}>
              <div className={styles.stateReady}>
                <span>Готово</span>
              </div>
              <button className={styles.btnCopy} onClick={handleRepost}>
                <span>
                  Скопировать ссылку{" "}
                  <img
                    className={styles.icon}
                    src={sendIcon}
                    alt="Иконка отправки"
                  />
                </span>
              </button>
            </div>
          ) : (
            <button
              className={styles.btnRepost}
              onClick={handleRepost}
              disabled={event.is_finished || event.is_canceled}
            >
              <span className={styles.btnText}>
                Поделиться ссылкой на мероприятие
              </span>
            </button>
          )}
        </div>
      </section>
      {isFinishModalOpen && (
        <FinishEventModal
          id={event.id}
          token={token}
          setIsFinishModalOpen={setIsFinishModalOpen}
        />
      )}
    </div>
  );
};
